import React, { useState } from "react"
import { Popover, Button } from "@shopify/polaris"
import { useTranslation } from "react-i18next"
import { useAppContext } from "components/AppContext"
import { useLocation } from "react-router-dom"

const SELECTED_GREEN = "#49A078"

const SortIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.83333 7.5L10 3.33333L14.1667 7.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.1667 12.5L10 16.6667L5.83333 12.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Chevron = ({ up }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path
      d={up ? "M5.83 12.5L10 8.33L14.17 12.5" : "M5.83 7.5L10 11.67L14.17 7.5"}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ArrowButton = ({ up, selected, disabled, onClick, label }) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "34px",
      height: "30px",
      borderRadius: "6px",
      border: selected ? "none" : "1px solid var(--p-color-border)",
      background: selected ? SELECTED_GREEN : "transparent",
      color: selected
        ? "#fff"
        : disabled
        ? "var(--p-color-text-disabled)"
        : "var(--p-color-text)",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
    }}
  >
    <Chevron up={up} />
  </button>
)

// scored_at / carbon / maturity only make sense once something is scored
const SCORE_REQUIRED = ["carbon", "maturity", "scored_at"]

const SortDropdown = () => {
  const { t } = useTranslation()
  const [popoverActive, setPopoverActive] = useState(false)
  const {
    treeSort = { field: null, direction: null },
    updateTreeSort,
    tree,
  } = useAppContext()
  const location = useLocation()

  const isOrganizePage = location.pathname.includes("/organize")
  const hasScoredNode = !!tree?.has_scored_node

  const togglePopover = () => setPopoverActive(!popoverActive)

  const sortFields = [
    { value: "carbon", label: t("dashboard:chart.sort.carbon") },
    { value: "maturity", label: t("dashboard:chart.sort.maturity") },
    { value: "name", label: t("dashboard:chart.sort.name") },
    { value: "employees", label: t("dashboard:chart.sort.employees") },
    { value: "scored_at", label: t("dashboard:chart.sort.scored_at") },
    { value: "updated_at", label: t("dashboard:chart.sort.updated_at") },
  ]

  const isDisabled = (field) =>
    SCORE_REQUIRED.includes(field) && !hasScoredNode

  const select = (field, direction) => {
    if (isDisabled(field)) return
    updateTreeSort(field, direction)
  }

  const clearSort = () => updateTreeSort(null, null)

  const activator = (
    <Button
      icon={SortIcon}
      accessibilityLabel={t("dashboard:chart.sort.title")}
      onClick={togglePopover}
      plain
      disabled={!isOrganizePage}
    />
  )

  if (!isOrganizePage) {
    return null
  }

  return (
    <Popover
      active={popoverActive}
      activator={activator}
      onClose={togglePopover}
      preferredAlignment="right"
      preferredPosition="below"
    >
      <div style={{ padding: "12px 16px", minWidth: "260px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: 600 }}>
            {t("dashboard:chart.sort.sort_by")}
          </span>
          <button
            type="button"
            onClick={clearSort}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: SELECTED_GREEN,
              fontSize: "13px",
              padding: 0,
            }}
          >
            ✕ {t("dashboard:chart.sort.clear")}
          </button>
        </div>

        {sortFields.map((field) => {
          const disabled = isDisabled(field.value)
          const isField = treeSort.field === field.value
          return (
            <div
              key={field.value}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 0",
              }}
            >
              <div
                style={{
                  color: disabled ? "var(--p-color-text-disabled)" : undefined,
                }}
              >
                <span style={{ fontSize: "14px" }}>{field.label}</span>
                {disabled && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "var(--p-color-text-disabled)",
                      marginLeft: "8px",
                    }}
                  >
                    {t("dashboard:chart.sort.score_required")}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                <ArrowButton
                  up
                  selected={isField && treeSort.direction === "asc"}
                  disabled={disabled}
                  onClick={() => select(field.value, "asc")}
                  label={`${field.label} ${t("dashboard:chart.sort.ascending")}`}
                />
                <ArrowButton
                  up={false}
                  selected={isField && treeSort.direction === "desc"}
                  disabled={disabled}
                  onClick={() => select(field.value, "desc")}
                  label={`${field.label} ${t("dashboard:chart.sort.descending")}`}
                />
              </div>
            </div>
          )
        })}
      </div>
    </Popover>
  )
}

export default SortDropdown
